import type { ProjectRightRailItemResponse } from '@protoss/shared';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';

interface UseSalesRightRailResult {
  items: ProjectRightRailItemResponse[];
  isLoading: boolean;
  error: string | null;
}

/**
 * useSalesRightRail - fetch the DB-driven right-rail panel catalog for the sales module.
 *
 * Calls GET /api/sales/right-rail, which returns right-rail panel items with
 * entitlement state resolved server-side. Panels are scoped to module_scope = 'sales'.
 *
 * The hook fetches once on mount. Entitlement state is workspace-level and does
 * not change during a session, so a single fetch per mount is sufficient.
 */
export function useSalesRightRail(): UseSalesRightRailResult {
  const [items, setItems] = React.useState<ProjectRightRailItemResponse[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRail = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiFetch<{ items: ProjectRightRailItemResponse[] }>(
        '/api/sales/right-rail'
      );
      setItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch right-rail items');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchRail();
  }, [fetchRail]);

  return { items, isLoading, error };
}
